// THE HOLDER'S OWN WEEK / MONTH / YEAR PASS ON A CLASS (D4.14 W3; backend `programs/passOut.mjs`) —
// `passOffer` on `GET …/programs/:programId/package-offer`, and the `kind: "pass"` rows of
// `/me/session-balances`: the one shape both send. Pure, so `classPassOffer.test.mjs` holds the rules.

export type ClassPassOffer = {
  purchaseId: string
  /** The club's own option name, else `Weekly pass` / `Monthly pass` / `Yearly pass`. */
  name: string
  /** The window, both days included (`programs/passWindow.mjs`). */
  coversFrom: string
  coversUntil: string
  paymentStatus: string
  /** Not paid AND bought by the holder's own side: the payment is theirs to finish. */
  awaiting: boolean
  /** Seats on the pass from the club's today on. */
  upcomingBooked: number
  /** The holder said "I've paid" and the club has not confirmed it yet (D4.14 follow-ups §2.4 item 2). */
  paymentClaimOpen: boolean
}

const DAY = /^\d{4}-\d{2}-\d{2}$/
const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

/** The wire's pass, or `null` for anything that is not a whole one (an older server sends none). */
export function readClassPassOffer(raw: unknown): ClassPassOffer | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const purchaseId = text(row.purchaseId)
  const coversFrom = text(row.coversFrom)
  const coversUntil = text(row.coversUntil)
  if (!purchaseId || !DAY.test(coversFrom) || !DAY.test(coversUntil)) return null
  const booked = Number(row.upcomingBooked)
  return {
    purchaseId,
    name: text(row.name) || 'Pass',
    coversFrom,
    coversUntil,
    paymentStatus: text(row.paymentStatus) || 'unpaid',
    awaiting: row.awaiting === true,
    upcomingBooked: Number.isFinite(booked) && booked > 0 ? Math.trunc(booked) : 0,
    paymentClaimOpen: row.paymentClaimOpen === true,
  }
}

/**
 * WHAT THE PASS OFFERS ON THE ENROL SHEET (§3.1 states): paid → its card; not paid and theirs to
 * finish → `awaiting`; not paid otherwise → the unpaid note (CP-7, Q3 = A); none → nothing.
 */
export type PassLane =
  | { kind: 'card'; pass: ClassPassOffer }
  | { kind: 'awaiting'; pass: ClassPassOffer }
  | { kind: 'unpaid'; pass: ClassPassOffer }
  | { kind: 'none' }

export function passLane(pass: ClassPassOffer | null): PassLane {
  if (!pass) return { kind: 'none' }
  if (pass.paymentStatus === 'paid') return { kind: 'card', pass }
  return pass.awaiting ? { kind: 'awaiting', pass } : { kind: 'unpaid', pass }
}

/** Is this session's DAY inside the pass, both ends included? */
export const inPassWindow = (pass: Pick<ClassPassOffer, 'coversFrom' | 'coversUntil'>, day: string) =>
  pass.coversFrom <= day && day <= pass.coversUntil

/** `POST …/enroll` by the pass, for ONE session: the reader's own, or their child's (`dependentId`). */
export function passEnrolBody(date: string, dependentId: string | null, purchaseId: string) {
  const child = String(dependentId ?? '').trim()
  return { date, passPurchaseId: purchaseId, ...(child ? { dependentId: child } : {}) }
}

/** The refusal's machine `code` (else its `reason`), `''` when the server said none. */
export function passRefusalCode(error: unknown): string {
  const payload = (error && typeof error === 'object' ? (error as { payload?: unknown }).payload : null) ?? {}
  const row = payload as Record<string, unknown>
  return text(row.code) || text(row.reason)
}
